import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CommentIcon from '@mui/icons-material/Comment';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import {
  BACKGROUND_COLORS,
  BORDER_STYLES,
  ROW_LABELS,
  IrrigationData,
  IrrigationProgramProps,
  ReadOnlyCellProps,
  EditableCellProps,
} from './utils';
import { fetchReadingsFromAPI } from '../../../services/readingsService';
import { ReduxState } from '../../../types';
import axios from '../../../utils/axios';
import ConversationModal from '../../../components/Highcharts/ConversationTab/conversationModal';
import { TimePointSchema, ConversationSchema } from '../../../requests/api/apiTypes';
import apiClient from '../../../requests/api/apiClient';
import { UserType, UserRole } from '../../../constants/user_types';

dayjs.locale('es');

const getBackgroundColor = (day: string, today: dayjs.Dayjs): string => {
  const date = dayjs(day, 'YYYY-MM-DD');

  if (date.isBefore(today, 'day')) {
    return BACKGROUND_COLORS.PAST;
  }

  if (date.isSame(today, 'day')) {
    return BACKGROUND_COLORS.CURRENT;
  }

  return BACKGROUND_COLORS.FUTURE;
};

const formatDayHeader = (day: string): { dayName: string; date: string } => {
  const dayjsDate = dayjs(day, 'YYYY-MM-DD');
  const dayName = dayjsDate.format('ddd');
  const date = dayjsDate.format('DD/MM');

  return {
    dayName: dayName.charAt(0).toUpperCase() + dayName.slice(1),
    date,
  };
};

const EditableCell: React.FC<EditableCellProps & {
  hasComment?: boolean;
  isCommentMode?: boolean;
  onClick?: () => void;
  onCommentClick?: () => void;
}> = ({
  value,
  isEditing,
  canEdit,
  backgroundColor,
  onEdit,
  onChange,
  onBlur,
  hasComment = false,
  isCommentMode = false,
  onClick,
  onCommentClick,
}) => {
  const handleCommentIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCommentClick) {
      onCommentClick();
    }
  };

  return (
      <TableCell
        align="center"
        sx={{
          borderRight: BORDER_STYLES.VERTICAL_THIN,
          borderBottom: BORDER_STYLES.NONE,
          backgroundColor,
          cursor: isCommentMode ? 'pointer' : 'default',
          position: 'relative',
          '&:hover': isCommentMode ? {
            backgroundColor: '#e3f2fd',
          } : {},
        }}
        onClick={isCommentMode ? onClick : undefined}
      >
        {hasComment && (
          <CommentIcon
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              fontSize: '12px',
              color: '#1976d2',
              cursor: 'pointer',
              zIndex: 10,
            }}
            onClick={handleCommentIconClick}
          />
        )}
        {isEditing ? (
          <TextField
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            size="small"
            autoFocus
            placeholder="mm"
            inputProps={{
              style: {
                textAlign: 'left', width: 40, fontSize: '0.875rem', zIndex: 2,
              },
              'aria-label': 'Cantidad en milímetros',
            }}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
          />
        ) : (
          <>
            <Box component="span">
              {value ? `${value} mm` : ''}
            </Box>
            {canEdit && (
              <IconButton size="small" onClick={onEdit} sx={{ ml: 0.5 }}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}
      </TableCell>
  );
};

const ReadOnlyCell: React.FC<ReadOnlyCellProps & {
  hasComment?: boolean;
  isCommentMode?: boolean;
  onClick?: () => void;
  onCommentClick?: () => void;
}> = ({
  value,
  backgroundColor,
  hasComment = false,
  isCommentMode = false,
  onClick,
  onCommentClick,
}) => {
  const handleCommentIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCommentClick) {
      onCommentClick();
    }
  };

  return (
      <TableCell
        align="center"
        sx={{
          borderRight: BORDER_STYLES.VERTICAL_THIN,
          borderBottom: BORDER_STYLES.NONE,
          backgroundColor,
          cursor: isCommentMode ? 'pointer' : 'default',
          position: 'relative',
          '&:hover': isCommentMode ? {
            backgroundColor: '#e3f2fd',
          } : {},
        }}
        onClick={isCommentMode ? onClick : undefined}
      >
        {hasComment && (
          <CommentIcon
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              fontSize: '12px',
              color: '#1976d2',
              cursor: 'pointer',
              zIndex: 10,
            }}
            onClick={handleCommentIconClick}
          />
        )}
        {value || ''}
      </TableCell>
  );
};

const IrrigationProgram: React.FC<IrrigationProgramProps> = ({ days, readOnly = false }) => {
  const today = dayjs();
  const { enqueueSnackbar } = useSnackbar();

  const [editingStates, setEditingStates] = useState({
    programmed: null as number | null,
    cdtec: null as number | null,
  });

  const [irrigationData, setIrrigationData] = useState<IrrigationData>({
    programmedMm: Array(days.length).fill(null),
    realMm: Array(days.length).fill(null),
    irrigationTimeHours: Array(days.length).fill(null),
    programmedCdtec: Array(days.length).fill(null),
  });

  const [originalData, setOriginalData] = useState<IrrigationData>({
    programmedMm: Array(days.length).fill(null),
    realMm: Array(days.length).fill(null),
    irrigationTimeHours: Array(days.length).fill(null),
    programmedCdtec: Array(days.length).fill(null),
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const currentUser = useSelector((state: ReduxState) => state.account.user);
  const isCdtec = currentUser?.type === UserType.CDTEC;
  const isAgro = currentUser?.role === UserRole.AGRO;

  const hasFullPrivileges = isCdtec && !isAgro;
  const canEditProgrammed = true;

  const [isCommentModeActive, setIsCommentModeActive] = useState(false);
  const [modalState, setModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [conversations, setConversations] = useState<ConversationSchema[]>([]);
  const [cellComments, setCellComments] = useState<Record<string, ConversationSchema>>({});

  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);

  const sectorToUse = useMemo(() => {
    if (!selectedSector || !selectedFarm) return null;

    let sector = selectedSector;
    if (selectedSector.parentSector && selectedSector.parentSector !== '1') {
      const parentSector = selectedFarm.sectors?.find(
        (s) => s.id === selectedSector.parentSector,
      );
      if (parentSector) {
        sector = parentSector;
      }
    }
    return sector;
  }, [selectedSector, selectedFarm]);

  const loadConversations = useCallback(async () => {
    if (!sectorToUse?.id) return;

    try {
      const { data } = await apiClient.conversation.conversationDetail(
        sectorToUse.id,
        'Programa de Riego',
      );
      setConversations(data.conversations || []);

      const commentsMap: Record<string, ConversationSchema> = {};
      data.conversations?.forEach((conv: ConversationSchema) => {
        if (conv.timePoint) {
          const dateKey = `${conv.timePoint.year}-${conv.timePoint.month.padStart(2, '0')}-${conv.timePoint.day.padStart(2, '0')}`;
          let cellType = 'programmed';
          if (conv.timePoint.hour === '12:00') {
            cellType = 'real';
          } else if (conv.timePoint.hour === '15:00') {
            cellType = 'cdtec';
          }
          const cellKey = `${dateKey}-${cellType}`;
          commentsMap[cellKey] = conv;
        }
      });
      setCellComments(commentsMap);
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  }, [sectorToUse?.id]);

  const handleCommentButtonClick = () => {
    setIsCommentModeActive(!isCommentModeActive);
    if (!isCommentModeActive) {
      enqueueSnackbar('Haz clic en una celda para agregar un comentario', { variant: 'info' });
    }
  };

  const getCellTypeHour = (cellType: 'programmed' | 'cdtec' | 'real'): string => {
    if (cellType === 'programmed') return '09:00';
    if (cellType === 'real') return '12:00';
    return '15:00';
  };

  const handleCellClick = (day: string, cellType: 'programmed' | 'cdtec' | 'real') => {
    const dayDate = dayjs(day, 'YYYY-MM-DD');
    const timePointData: TimePointSchema = {
      hour: getCellTypeHour(cellType),
      day: dayDate.format('DD'),
      month: dayDate.format('MM'),
      year: dayDate.format('YYYY'),
    };

    const cellKey = `${day}-${cellType}`;
    const existingComment = cellComments[cellKey];

    if (existingComment) {
      setModalState({
        open: true,
        conversationId: existingComment.conversationId,
        timePointData,
      });
    } else {
      setModalState({
        open: true,
        conversationId: '',
        timePointData,
      });
    }

    setIsCommentModeActive(false);
  };

  const fetchIdRef = useRef(0);

  const getEmptyData = useCallback((): IrrigationData => ({
    programmedMm: Array(days.length).fill(null),
    realMm: Array(days.length).fill(null),
    irrigationTimeHours: Array(days.length).fill(null),
    programmedCdtec: Array(days.length).fill(null),
  }), [days]);

  useEffect(() => {
    if (!sectorToUse) {
      setIrrigationData(getEmptyData());
      setOriginalData(getEmptyData());
      setHasChanges(false);
      setLoading(false);
      return () => {};
    }

    const sectorId = sectorToUse.id as string;
    const farmId = selectedFarm!.id as string;
    const { wiseconnId } = sectorToUse;

    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [
          irrigationProgramData,
          realIrrigationReadings,
          irrigationTimeReadings,
        ] = await Promise.all([
          axios.get(`/api/sector/${sectorId}/irrigation-program`)
            .then((r) => r.data?.irrigationProgram || {})
            .catch(() => ({})),
          wiseconnId ? fetchReadingsFromAPI({
            farmId,
            startDate: days[0],
            endDate: days[days.length - 1],
            wiseconnZoneId: wiseconnId,
            name: 'Irrigation Precipitation',
            granularity: 'daily',
          }) : Promise.resolve([]),
          wiseconnId ? fetchReadingsFromAPI({
            farmId,
            startDate: days[0],
            endDate: days[days.length - 1],
            wiseconnZoneId: wiseconnId,
            name: 'Irrigation Time',
            granularity: 'daily',
          }) : Promise.resolve([]),
        ]);

        if (cancelled || fetchIdRef.current !== currentFetchId) return;

        const realIrrigationMap: Record<string, number> = {};
        realIrrigationReadings.forEach((reading: any) => {
          const date = dayjs(reading.date).format('YYYY-MM-DD');
          if (reading.cumulativeTotal > 0) {
            realIrrigationMap[date] = reading.cumulativeTotal;
          }
        });

        const irrigationTimeMap: Record<string, number> = {};
        irrigationTimeReadings.forEach((reading: any) => {
          const date = dayjs(reading.date).format('YYYY-MM-DD');
          if (reading.cumulativeTotal > 0) {
            irrigationTimeMap[date] = reading.cumulativeTotal / 60;
          }
        });

        const newIrrigationData: IrrigationData = getEmptyData();

        days.forEach((day, index) => {
          const realValue = realIrrigationMap[day];
          if (realValue !== undefined) {
            newIrrigationData.realMm[index] = `${realValue.toFixed(1)} mm`;
          }

          const timeValue = irrigationTimeMap[day];
          if (timeValue !== undefined) {
            newIrrigationData.irrigationTimeHours[index] = `${timeValue.toFixed(1)} hrs`;
          }

          const programData = irrigationProgramData[day];
          if (programData) {
            if (programData.programmedMm) {
              newIrrigationData.programmedMm[index] = programData.programmedMm;
            }
            if (programData.programmedCdtec) {
              newIrrigationData.programmedCdtec[index] = programData.programmedCdtec;
            }
          }
        });

        setIrrigationData(newIrrigationData);
        setOriginalData(JSON.parse(JSON.stringify(newIrrigationData)));
        setHasChanges(false);
      } catch (err) {
        if (cancelled || fetchIdRef.current !== currentFetchId) return;
        console.error('Error fetching irrigation data:', err);
        setError('Error al cargar los datos de riego');
      } finally {
        if (!cancelled && fetchIdRef.current === currentFetchId) {
          setLoading(false);
        }
      }
    };

    fetchData();
    loadConversations();

    return () => { cancelled = true; };
  }, [sectorToUse?.id, sectorToUse?.wiseconnId, selectedFarm?.id, days]);

  useEffect(() => {
    const dataChanged = JSON.stringify(irrigationData) !== JSON.stringify(originalData);
    setHasChanges(dataChanged);
  }, [irrigationData, originalData]);

  useEffect(() => {
    const commentsMap: Record<string, ConversationSchema> = {};
    conversations.forEach((conv: ConversationSchema) => {
      if (conv.timePoint) {
        const dateKey = `${conv.timePoint.year}-${conv.timePoint.month.padStart(2, '0')}-${conv.timePoint.day.padStart(2, '0')}`;
        let cellType = 'programmed';
        if (conv.timePoint.hour === '12:00') {
          cellType = 'real';
        } else if (conv.timePoint.hour === '15:00') {
          cellType = 'cdtec';
        }
        const cellKey = `${dateKey}-${cellType}`;
        commentsMap[cellKey] = conv;
      }
    });
    setCellComments(commentsMap);
  }, [conversations]);

  const handleEdit = (rowType: 'programmed' | 'cdtec', index: number) => {
    setEditingStates((prev) => ({ ...prev, [rowType]: index }));
  };

  const handleChange = (rowType: 'programmed' | 'cdtec', index: number, value: string) => {
    const dataKey = rowType === 'programmed' ? 'programmedMm' : 'programmedCdtec';
    setIrrigationData((prev) => {
      const newData = [...prev[dataKey]];
      newData[index] = value;
      return { ...prev, [dataKey]: newData };
    });
  };

  const handleBlur = (rowType: 'programmed' | 'cdtec') => {
    setEditingStates((prev) => ({ ...prev, [rowType]: null }));
  };

  const handleSave = async () => {
    if (!sectorToUse) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const irrigationProgramData: Record<string, {
        programmedMm?: string;
        programmedCdtec?: string
      }> = {};

      days.forEach((day, index) => {
        const programmedMm = irrigationData.programmedMm[index];
        const programmedCdtec = irrigationData.programmedCdtec[index];

        if (programmedMm || programmedCdtec) {
          irrigationProgramData[day] = {};
          if (programmedMm) irrigationProgramData[day].programmedMm = programmedMm;
          if (programmedCdtec) irrigationProgramData[day].programmedCdtec = programmedCdtec;
        }
      });

      await axios.put(`/api/sector/${sectorToUse.id}/irrigation-program`, {
        irrigationProgramData,
      });

      setOriginalData(JSON.parse(JSON.stringify(irrigationData)));
      setHasChanges(false);
      setSuccess('Programa de riego guardado exitosamente');

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving irrigation program:', err);
      setError('Error al guardar el programa de riego');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const renderHeaderRow = () => (
    <TableRow>
      <TableCell sx={{ borderRight: BORDER_STYLES.VERTICAL_THICK }}>
        Día
      </TableCell>
      {days.map((day, idx) => {
        const headerData = formatDayHeader(day);
        return (
          <TableCell
            key={idx}
            align="center"
            sx={{
              borderRight: BORDER_STYLES.VERTICAL_THIN,
              backgroundColor: getBackgroundColor(day, today),
              whiteSpace: 'pre-line',
              lineHeight: 1.2,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {headerData.dayName}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {headerData.date}
              </Typography>
            </Box>
          </TableCell>
        );
      })}
    </TableRow>
  );

  const renderEditableRow = (rowType: 'programmed' | 'cdtec') => {
    const dataKey = rowType === 'programmed' ? 'programmedMm' : 'programmedCdtec';
    const values = irrigationData[dataKey];
    const editingIndex = editingStates[rowType];

    return (
      <TableRow>
        <TableCell
          sx={{
            borderRight: BORDER_STYLES.VERTICAL_THICK,
            borderBottom: BORDER_STYLES.NONE,
          }}
        >
          {ROW_LABELS[rowType]}
        </TableCell>
        {days.map((day, idx) => {
          let canEdit = false;
          if (!readOnly) {
            canEdit = rowType === 'programmed' ? canEditProgrammed : hasFullPrivileges;
          }
          const cellKey = `${day}-${rowType}`;
          const hasComment = !!cellComments[cellKey];

          return (
            <EditableCell
              key={idx}
              value={values[idx]}
              isEditing={editingIndex === idx}
              canEdit={canEdit}
              backgroundColor={getBackgroundColor(day, today)}
              onEdit={() => handleEdit(rowType, idx)}
              onChange={(value) => handleChange(rowType, idx, value)}
              onBlur={() => handleBlur(rowType)}
              hasComment={hasComment}
              isCommentMode={isCommentModeActive}
              onClick={() => isCommentModeActive && handleCellClick(day, rowType)}
              onCommentClick={() => handleCellClick(day, rowType)}
            />
          );
        })}
      </TableRow>
    );
  };

  const parseMmValue = (value: string | null): number | null => {
    if (!value) return null;
    const num = parseFloat(value.replace(' mm', ''));
    return Number.isNaN(num) ? null : num;
  };

  const getDifferenceColor = (diff: number): string => {
    if (diff > 0) return '#d32f2f';
    if (diff < 0) return '#1976d2';
    return '#2e7d32';
  };

  const renderDifferenceRow = () => (
    <TableRow>
      <TableCell
        sx={{
          borderRight: BORDER_STYLES.VERTICAL_THICK,
          borderBottom: BORDER_STYLES.NONE,
        }}
      >
        {ROW_LABELS.difference}
      </TableCell>
      {days.map((day, idx) => {
        const cdtecVal = parseMmValue(irrigationData.programmedCdtec[idx]);
        const realVal = parseMmValue(irrigationData.realMm[idx]);

        let content = '';
        let color = 'inherit';

        if (cdtecVal !== null && realVal !== null) {
          const diff = cdtecVal - realVal;
          const pct = cdtecVal !== 0
            ? ((diff / cdtecVal) * 100).toFixed(0)
            : '0';
          color = getDifferenceColor(diff);
          content = `${diff > 0 ? '+' : ''}${diff.toFixed(1)} (${pct}%)`;
        }

        return (
          <TableCell
            key={idx}
            align="center"
            sx={{
              borderRight: BORDER_STYLES.VERTICAL_THIN,
              borderBottom: BORDER_STYLES.NONE,
              backgroundColor: getBackgroundColor(day, today),
              color,
              fontWeight: content ? 600 : 400,
            }}
          >
            {content}
          </TableCell>
        );
      })}
    </TableRow>
  );

  const renderReadOnlyRow = () => (
    <TableRow>
      <TableCell
        sx={{
          borderRight: BORDER_STYLES.VERTICAL_THICK,
          borderBottom: BORDER_STYLES.NONE,
        }}
      >
        {ROW_LABELS.real}
      </TableCell>
      {days.map((day, idx) => {
        const cellKey = `${day}-real`;
        const hasComment = !!cellComments[cellKey];

        return (
          <ReadOnlyCell
            key={idx}
            value={irrigationData.realMm[idx]}
            backgroundColor={getBackgroundColor(day, today)}
            hasComment={hasComment}
            isCommentMode={isCommentModeActive}
            onClick={() => isCommentModeActive && handleCellClick(day, 'real')}
            onCommentClick={() => handleCellClick(day, 'real')}
          />
        );
      })}
    </TableRow>
  );

  const getTheoreticalPrecipitation = (): number | null => {
    const value = sectorToUse?.irrigationConfig?.theoreticalPrecipitation;
    if (!value) return null;
    const num = parseFloat(value);
    return Number.isNaN(num) || num === 0 ? null : num;
  };

  const renderTheoreticalTimeRow = () => {
    const theoreticalPrecipitation = getTheoreticalPrecipitation();

    return (
      <TableRow>
        <TableCell
          sx={{
            borderRight: BORDER_STYLES.VERTICAL_THICK,
            borderBottom: BORDER_STYLES.NONE,
          }}
        >
          {ROW_LABELS.theoreticalTime}
        </TableCell>
        {days.map((day, idx) => {
          const cdtecVal = parseMmValue(irrigationData.programmedCdtec[idx]);
          let content = '';

          if (cdtecVal !== null && theoreticalPrecipitation !== null) {
            const hours = cdtecVal / theoreticalPrecipitation;
            content = `${hours.toFixed(1)} hrs`;
          }

          return (
            <TableCell
              key={idx}
              align="center"
              sx={{
                borderRight: BORDER_STYLES.VERTICAL_THIN,
                borderBottom: BORDER_STYLES.NONE,
                backgroundColor: getBackgroundColor(day, today),
              }}
            >
              {content}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  const renderIrrigationTimeRow = () => (
    <TableRow>
      <TableCell
        sx={{
          borderRight: BORDER_STYLES.VERTICAL_THICK,
          borderBottom: BORDER_STYLES.NONE,
        }}
      >
        {ROW_LABELS.irrigationTime}
      </TableCell>
      {days.map((day, idx) => (
        <ReadOnlyCell
          key={idx}
          value={irrigationData.irrigationTimeHours[idx]}
          backgroundColor={getBackgroundColor(day, today)}
        />
      ))}
    </TableRow>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <Typography>Cargando datos de riego...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {(error || success) && (
        <Box mb={2}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
        </Box>
      )}

      {!readOnly && (
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} width="100%">
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? 'GUARDANDO...' : 'GUARDAR CAMBIOS'}
            </Button>
            <Button
              variant="contained"
              color={isCommentModeActive ? 'secondary' : 'primary'}
              onClick={handleCommentButtonClick}
            >
              {isCommentModeActive ? 'CANCELAR COMENTARIO' : 'AGREGAR COMENTARIO A CELDA'}
            </Button>
          </Box>
        </Box>
      )}

      {hasChanges && (
        <Box mb={2}>
          <Typography color="warning.main" variant="body2">
            Tienes cambios sin guardar
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{
            width: '100%',
            minWidth: '100%',
            tableLayout: 'fixed',
            '& td, & th': {
              borderRight: BORDER_STYLES.VERTICAL_THIN,
              padding: '8px 4px',
              fontSize: '0.875rem',
              textAlign: 'center',
            },
            '& td:first-of-type, & th:first-of-type': {
              width: '90px',
              minWidth: '90px',
              maxWidth: '90px',
            },
            '& td:not(:first-of-type), & th:not(:first-of-type)': {
              width: `calc((100% - 90px) / ${days.length})`,
              minWidth: '80px',
            },
            '& .MuiTableBody-root td': {
              borderBottom: BORDER_STYLES.NONE,
            },
            '& .MuiTableHead-root th': {
              borderBottom: BORDER_STYLES.HORIZONTAL_HEADER,
              padding: '12px 4px',
              fontSize: '0.875rem',
              height: '80px',
              verticalAlign: 'middle',
            },
          }}
        >
          <TableHead>
            {renderHeaderRow()}
          </TableHead>

          <TableBody>
            {renderEditableRow('programmed')}
            {renderReadOnlyRow()}
            {renderEditableRow('cdtec')}
            {renderDifferenceRow()}
            {renderIrrigationTimeRow()}
            {renderTheoreticalTimeRow()}
          </TableBody>
        </Table>
      </TableContainer>

      {modalState.open && selectedSector && selectedFarm && (
        <ConversationModal
          modalState={modalState}
          setModalState={(newState) => {
            setModalState(newState);
            if (typeof newState === 'object' && !newState.open) {
              loadConversations();
            }
          }}
          farmId={selectedFarm.id!}
          sectorId={sectorToUse?.id || selectedSector.id!}
          chart="Programa de Riego"
          setConversationsFunction={(newConversations) => {
            setConversations(newConversations);
            loadConversations();
          }}
        />
      )}
    </Box>
  );
};

export default IrrigationProgram;
